import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [loadingRandom, setLoadingRandom] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 20 - 10,
        y: (e.clientY / window.innerHeight) * 20 - 10,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleRandomTerror = async () => {
    setLoadingRandom(true);
    try {
      const { data, error } = await supabase
        .from('stories')
        .select('id')
        .eq('status', 'published');

      if (error) throw error;
      if (data && data.length > 0) {
        const random = data[Math.floor(Math.random() * data.length)];
        navigate(`/story/${random.id}`);
      }
    } catch (err) {
      console.error('Failed to fetch random story:', err);
    } finally {
      setLoadingRandom(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-100"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/32ad3222048c829f1fd1d6c65fd13bf7.jpg)',
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) scale(1.1)`,
        }}
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>

      {/* Animated Fog Effect */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-t from-red-900/20 via-transparent to-transparent animate-pulse"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto w-full pt-8 md:pt-4">
        <div className="mb-1 md:mb-2 animate-fade-in">
          <img
            src="https://static.readdy.ai/image/cd80e8551adaa83aa4e45d1e4f56c590/c334bfc7bb42624cba9a352752f4949a.png"
            alt="Sinister Files"
            className="w-72 h-72 md:w-[640px] md:h-[640px] mx-auto object-contain drop-shadow-2xl"
          />
        </div>

        <h1 className="text-6xl md:text-7xl font-bold text-red-600 mb-4 font-['Cinzel'] tracking-wider drop-shadow-2xl">
          SINISTER FILES
        </h1>

        <p className="text-2xl md:text-3xl text-gray-300 mb-6 font-['Crimson_Text'] italic">
          Unearthing Dark Stories
        </p>

        <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-3xl mx-auto font-['Crimson_Text'] leading-relaxed">
          Dive into a collection of <strong>user-generated horror stories</strong> that will keep you awake at night. 
          From psychological terror to supernatural encounters, discover tales that blur the line between fiction and reality.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link
            to="/stories"
            className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold rounded-lg transition-all duration-300 shadow-2xl shadow-red-900/50 hover:shadow-red-900/70 hover:scale-105 font-['Crimson_Text'] whitespace-nowrap cursor-pointer"
          >
            <i className="ri-book-open-line mr-2"></i>
            Explore Stories
          </Link>
          <button
            className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white text-lg font-semibold rounded-lg transition-all duration-300 shadow-2xl shadow-black/50 hover:shadow-black/70 hover:scale-105 font-['Crimson_Text'] whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleRandomTerror}
            disabled={loadingRandom}
          >
            <i className={`${loadingRandom ? 'ri-loader-4-line animate-spin' : 'ri-shuffle-line'} mr-2`}></i>
            {loadingRandom ? 'Finding...' : 'Random Terror'}
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-red-500"></i>
      </div>
    </section>
  );
};

export default HeroSection;